import { BaseComponent } from '../base-component.js';
import { api } from '../../client/api.js';
import { tracker } from '../../client/tracking.js';
import { escapeHtml, escapeAttribute, sanitizeUrl, resolveImageUrl, hexToContrastColor } from '../../shared/utils.js';
import type { UserPublic, PageConfig, Link, FeaturedBlockData } from '../../shared/types.js';
import componentStyles from './landing-page.css?inline';

const CSS_COLOR_REGEX = /^#[0-9a-fA-F]{3,8}$/;
const ALLOWED_FONTS = [
  'system-ui, sans-serif',
  'Arial, sans-serif',
  'Helvetica, sans-serif',
  'Georgia, serif',
  'Times New Roman, serif',
  'Courier New, monospace',
  'Verdana, sans-serif',
  'Trebuchet MS, sans-serif',
];

function sanitizeCssColor(color: string | undefined, fallback: string): string {
  if (!color || typeof color !== 'string') return fallback;
  return CSS_COLOR_REGEX.test(color.trim()) ? color.trim() : fallback;
}

function sanitizeCssFont(font: string | undefined, fallback: string): string {
  if (!font || typeof font !== 'string') return fallback;
  return ALLOWED_FONTS.includes(font) ? font : fallback;
}

export class LandingPage extends BaseComponent {
  private slug = '';
  private user: UserPublic | null = null;
  private config: PageConfig | null = null;
  private links: Link[] = [];
  private loading = true;
  private error = '';

  static get observedAttributes(): string[] {
    return ['slug'];
  }

  attributeChangedCallback(name: string, _oldValue: string, newValue: string): void {
    if (name === 'slug' && newValue !== this.slug) {
      this.slug = newValue;
      this.loadData();
    }
  }

  connectedCallback(): void {
    super.connectedCallback();
  }

  protected styles(): string {
    const c = this.config;
    const bgColor = sanitizeCssColor(c?.backgroundColor, '#ffffff');
    const textColor = hexToContrastColor(bgColor);
    const accentColor = sanitizeCssColor(c?.accentColor, '#3b82f6');
    const fontFamily = sanitizeCssFont(c?.fontFamily, 'system-ui, sans-serif');
    const bgUrl = c?.backgroundUrl ? resolveImageUrl(c.backgroundUrl) : '';
    const bgBlur = c?.backgroundBlur ?? 0;
    const borderEnabled = c?.logoBorderEnabled ?? true;
    const borderWidth = c?.logoBorderWidth ?? 4;
    const borderColor = sanitizeCssColor(c?.logoBorderColor, '#ffffff');
    const borderStyle = borderEnabled ? `${borderWidth}px solid ${borderColor}` : 'none';

    const dynamicStyles = `
      :host {
        display: block;
        min-height: 100vh;
        background: ${bgColor};
        color: ${textColor};
        font-family: ${fontFamily};
        --page-bg: ${bgColor};
        --page-text: ${textColor};
      }
      :host([has-bg]) {
        color: #ffffff;
        --page-text: #ffffff;
      }
      .page-bg {
        position: fixed;
        top: 0; left: 0; width: 100%; height: 100%;
        background-image: url(${escapeAttribute(bgUrl)});
        background-size: cover;
        background-position: center;
        ${bgBlur > 0 ? `filter: blur(${bgBlur}px); transform: scale(1.05);` : ''}
        z-index: 1;
        pointer-events: none;
      }
      .logo, .avatar {
        border: ${borderStyle};
      }
      .avatar {
        background: ${accentColor};
        color: ${hexToContrastColor(accentColor)};
      }
      .link {
        background: ${accentColor};
        color: ${hexToContrastColor(accentColor)};
      }
    `;

    return componentStyles + dynamicStyles;
  }

  protected template(): string {
    if (this.loading) {
      return '<div class="loading">Loading...</div>';
    }

    if (this.error || !this.user) {
      return `
        <div class="error">
          <h1>Page Not Found</h1>
          <p>The page you're looking for doesn't exist.</p>
        </div>
      `;
    }

    const displayName = this.config?.title || this.user.name;
    const initial = displayName.charAt(0).toUpperCase();
    const safeDisplayName = escapeHtml(displayName);
    const safeInitial = escapeHtml(initial);
    const safeBio = this.config?.bio ? escapeHtml(this.config.bio) : '';
    const safeLogoUrl = this.config?.logoUrl ? resolveImageUrl(this.config.logoUrl) : '';
    const logoShape = this.config?.logoShape || 'circle';

    const featuredBlockData = this.config?.blocks?.find(b => b.type === 'featured');
    let featuredBlockAbove = '';
    let featuredBlockBelow = '';

    if (featuredBlockData && featuredBlockData.content) {
      try {
        const blockData: FeaturedBlockData = JSON.parse(featuredBlockData.content);
        if (blockData.visible !== false) {
          const featuredHtml = this.renderFeaturedBlock(blockData);
          if (blockData.position === 'above') {
            featuredBlockAbove = featuredHtml;
          } else {
            featuredBlockBelow = featuredHtml;
          }
        }
      } catch {
        // Invalid JSON, skip featured block
      }
    }

    return `
      ${this.config?.backgroundUrl ? '<div class="page-bg"></div>' : ''}
      <div class="profile-container">
        <div class="profile">
          ${safeLogoUrl
        ? `<img src="${escapeAttribute(safeLogoUrl)}" alt="${escapeAttribute(displayName)}" class="logo logo--${logoShape}">`
        : `<div class="avatar logo--${logoShape}">${safeInitial}</div>`
      }
          <h1 class="title">${safeDisplayName}</h1>
          ${safeBio ? `<p class="bio">${safeBio}</p>` : ''}
        </div>
        
        ${featuredBlockAbove}
        
        <ul class="links">
          ${this.links.map((link) => {
        const safeUrl = sanitizeUrl(link.url);
        const safeTitle = escapeHtml(link.title);
        return `
            <li class="link-item">
              <a href="${escapeAttribute(safeUrl)}" target="_blank" rel="noopener noreferrer" class="link">
                ${safeTitle}
              </a>
            </li>
          `;
      }).join('')}
        </ul>
        
        ${featuredBlockBelow}

        ${this.renderDisclaimer(this.config?.disclaimer, this.config?.disclaimerTitle)}

        <div class="branding">
          <a href="/" target="_blank">Powered by Agentor</a>
        </div>
      </div>
    `;
  }

  private renderDisclaimer(content: string | undefined, title: string | undefined): string {
    if (!content?.trim()) return '';
    if (this.config?.disclaimerVisible === false) return '';
    const safeTitle = escapeHtml(title?.trim() || 'Disclaimer');
    const safeContent = escapeHtml(content);
    return `
      <div class="disclaimer">
        <button type="button" class="disclaimer-toggle" aria-expanded="false">${safeTitle}</button>
        <div class="disclaimer-content" hidden>
          <p class="disclaimer-text">${safeContent}</p>
        </div>
      </div>
    `;
  }

  private renderFeaturedBlock(data: FeaturedBlockData): string {
    const safeTitle = escapeHtml(data.title);
    const safeDescription = escapeHtml(data.description);
    const safeImageUrl = data.imageUrl ? resolveImageUrl(data.imageUrl) : '';
    const safeUrl = data.linkUrl ? sanitizeUrl(data.linkUrl) : '';
    const tag = safeUrl ? 'a' : 'div';
    const hrefAttr = safeUrl
      ? `href="${escapeAttribute(safeUrl)}" target="_blank" rel="noopener noreferrer"`
      : '';

    return `
      <${tag} class="featured-block" ${hrefAttr}>
        <div class="featured-content">
          <h2 class="featured-title">${safeTitle}</h2>
          <p class="featured-description">${safeDescription}</p>
        </div>
        ${safeImageUrl ? `
          <div class="featured-image">
            <img src="${escapeAttribute(safeImageUrl)}" alt="${escapeAttribute(data.title)}">
          </div>
        ` : ''}
      </${tag}>
    `;
  }

  private async loadData(): Promise<void> {
    if (!this.slug) return;

    this.loading = true;
    this.error = '';
    this.render();

    try {
      const [userResult, configResult, linksResult] = await Promise.all([
        api.getProfile(this.slug),
        api.getPublicPageConfig(this.slug),
        api.getPublicLinks(this.slug),
      ]);

      if (!userResult.success || !userResult.data) {
        this.error = 'User not found';
      } else {
        this.user = userResult.data;
        this.config = configResult.data || null;
        this.links = linksResult.data || [];

        if (this.config?.backgroundUrl) {
          this.setAttribute('has-bg', '');
        } else {
          this.removeAttribute('has-bg');
        }

        const isPublicPage = window.location.pathname.startsWith('/u/');
        if (this.user && isPublicPage) {
          tracker.pageView(this.user.id);
        }
      }
    } catch (err) {
      this.error = (err as Error).message;
    }

    this.loading = false;
    this.updateStyles();
    this.render();
    this.afterRender();
  }

  protected afterRender(): void {
    if (!this.user) return;

    this.$all('.link').forEach((linkEl, index) => {
      linkEl.addEventListener('click', () => {
        const link = this.links[index];
        if (link && this.user) {
          tracker.linkClick(this.user.id, link.id);
        }
      });
    });

    const toggle = this.$('.disclaimer-toggle');
    if (toggle) {
      toggle.addEventListener('click', () => {
        const content = this.$('.disclaimer-content');
        const expanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!expanded));
        content?.toggleAttribute('hidden');
      });
    }
  }

  private updateStyles(): void {
    const componentStyles = this.styles();
    if (componentStyles) {
      const componentSheet = new CSSStyleSheet();
      componentSheet.replaceSync(componentStyles);
      this.shadow.adoptedStyleSheets = [...this.shadow.adoptedStyleSheets.slice(0, -1), componentSheet];
    }
  }
}

customElements.define('landing-page', LandingPage);
